import React, { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "../styles/components/profile-page.scss";
import { fetchSteamProfile } from "../services/SteamService";
import { fetchMyVdoLink, fetchPlayerVdoLink, VdoLinkResponse } from "../services/vdoService";
import axios from "axios";
import { baseURL, baseURLnoapi, powerStatsAPI, CURRENT_SEASON } from "../Constants";
import {
	FaTwitter,
	FaSteam,
	FaDiscord,
	FaEdit,
	FaTrophy,
	FaVideo,
	FaCopy,
} from "react-icons/fa";
import { SiFaceit } from "react-icons/si";
import PlayerHighlightsSection from "../components/player/PlayerHighlightsSection";

// Types
interface SteamProfile {
	avatarMedium: string;
	profileUrl?: string;
}

interface Team {
	id: string;
	name: string;
	logo: string | null;
	players?: any[];
}

interface AccountDetails {
	steamID: string;
	name?: string;
	dateOfBirth?: string;
	playerName: string;
	twitter?: string;
	faceit?: string;
	role?: string;
	team?: Team | null;
	teamSeason?: number | null;
	currentSeason?: number;
	isAdmin: boolean;
	passwordHash?: string;
	discordId?: string;
	discordUsername?: string;
	age?: number;
}

interface PlayerStats {
	kills: number;
	deaths: number;
	assists: number;
	kdr: number;
	adr: number;
	matches: number;
}

interface SeasonStats {
	season: number;
	stats: PlayerStats;
	teamName?: string;
	placement?: number;
	// Additional stats from the Rising PowerStats API
	hltv2Rating?: number;
	hsPercentage?: number;
	kast?: number;
	firstKills?: number;
	// Advanced PowerStats statistics
	openingDuelAttemptPercentage?: number;
	openingDuelWinPercentage?: number;
	utilityDamage?: number;
	enemiesFlashed?: number;
}

interface MatchResult {
	id: string;
	date: string;
	teamAName: string;
	teamAScore: number;
	teamBName: string;
	teamBScore: number;
	mapName: string;
	isBo3Series?: boolean;
	playerStats?: {
		kills: number;
		deaths: number;
		assists: number;
		adr: number;
	};
}

// Helper functions
const formatTime = (dateString: string) => {
	const date = new Date(dateString);
	return date.toLocaleTimeString("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
};

const formatDate = (dateString: string) => {
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
};

const formatMapName = (mapName: string) => {
	// Remove "de_" prefix if present
	const cleaned = mapName.replace(/^de_/, '');
	// Capitalize first letter
	return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

// MVP achievements - temporary hardcoded data until backend system is implemented
const MVP_ACHIEVEMENTS: { [steamId: string]: number[] } = {
	'76561198005496143': [1], // k0bra - Season 1 MVP
	'76561198192433606': [2], // Davud - Season 2 MVP
};

// Function to determine tournament placements based on bracket results
const getTournamentPlacements = (matches: any[], season: number) => {
	const placements: { [teamId: string]: { placement: number; teamName: string } } = {};
	
	// Find finals match (match 14)
	const finalsMatch = matches.find(match => 
		match.bracket_index === 14 && 
		match.season === season &&
		match.team1score !== null && 
		match.team2score !== null
	);
	
	if (finalsMatch) {
		// Determine winner and runner-up from finals
		const team1Won = finalsMatch.team1score > finalsMatch.team2score;
		const winnerTeam = team1Won ? finalsMatch.team1 : finalsMatch.team2;
		const runnerUpTeam = team1Won ? finalsMatch.team2 : finalsMatch.team1;
		
		placements[winnerTeam.teamId] = { placement: 1, teamName: winnerTeam.teamName };
		placements[runnerUpTeam.teamId] = { placement: 2, teamName: runnerUpTeam.teamName };
		
		// Find semi-finals matches (typically match 12 and 13)
		const semiFinalsMatches = matches.filter(match => 
			(match.bracket_index === 12 || match.bracket_index === 13) &&
			match.season === season &&
			match.team1score !== null && 
			match.team2score !== null
		);
		
		// Teams that lost in semi-finals get 3rd place (tied)
		semiFinalsMatches.forEach(match => {
			const team1Won = match.team1score > match.team2score;
			const loserTeam = team1Won ? match.team2 : match.team1;
			
			// Only add if not already placed (weren't in finals)
			if (!placements[loserTeam.teamId]) {
				placements[loserTeam.teamId] = { placement: 3, teamName: loserTeam.teamName };
			}
		});
	}
	
	return placements;
};

const ProfilePage: React.FC = () => {
	const { steamId } = useParams<{ steamId: string }>();
	const { user } = useContext(AuthContext);

	// State variables
	const [steamProfile, setSteamProfile] = useState<SteamProfile | null>(null);
	const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(
		null
	);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
	const [seasonStats, setSeasonStats] = useState<SeasonStats[]>([]);
	const [recentMatches, setRecentMatches] = useState<MatchResult[]>([]);
	const [loadingStats, setLoadingStats] = useState<boolean>(false);
	const [selectedSeason, setSelectedSeason] = useState<number | null>(null); // Start with null, will be set after detecting seasons
	const [availableSeasons, setAvailableSeasons] = useState<number[]>([]); // Track which seasons have data
	const [showUnlinkModal, setShowUnlinkModal] = useState<boolean>(false); // Modal state for Discord unlink confirmation
	const [detectingSeasons, setDetectingSeasons] = useState<boolean>(true);
	const [vdoLinks, setVdoLinks] = useState<VdoLinkResponse | null>(null); // VDO.ninja links
	const [showVdoModal, setShowVdoModal] = useState<boolean>(false); // Modal state for VDO links (admin view)
	const [vdoLoading, setVdoLoading] = useState<boolean>(false); // Loading state for VDO links
	const teamPlaceholderImage =
		"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

	// Define steamCallbackUrl using the constants
	const steamCallbackUrl = baseURLnoapi + "/auth/";

	const isOwnProfile = user && steamId === user.steamID;

	// Helper function to get team logo URL
	const getTeamLogoUrl = (team: Team) => {
		if (team.logo && team.logo.startsWith("http")) {
			return team.logo;
		} else if (team.logo) {
			return `${baseURLnoapi}${team.logo}`;
		} else {
			// Get logo from the team logo endpoint
			return `${baseURL}/Team/${team.id}/logo`;
		}
	};

	// Check for Discord linking callback
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const discordLinked = urlParams.get("discord");

		if (discordLinked === "linked") {
			// Check if there's a JWT token in the URL (for authentication persistence)
			const urlPath = window.location.href;
			const tokenStart =
				urlPath.indexOf(steamCallbackUrl) + steamCallbackUrl.length;
			const tokenEnd = urlPath.indexOf("&discord=linked");

			if (tokenStart > 0 && tokenEnd > tokenStart) {
				// Extract and save the token
				const token = urlPath.substring(tokenStart, tokenEnd);
				if (token) {
					localStorage.setItem("token", token);
					// Refresh the page to update user data
					window.location.reload();
				}
			}

			// Show success message
			alert("Discord account successfully linked!");
			// Remove the parameter from URL to prevent showing the message on refresh
			window.history.replaceState({}, document.title, window.location.pathname);
		}
	}, [steamCallbackUrl]);

	// Load profile data, account details, and avatar
	useEffect(() => {
		const loadProfileData = async () => {
			setLoading(true);
			try {
				// Parallel requests for better performance
				const requests = [];

				// 1. Fetch Steam profile
				if (steamId) {
					requests.push(
						fetchSteamProfile(steamId).then((profile) => {
							setSteamProfile(profile);
						})
					);
				}

				// 2. Fetch account details from our backend
				requests.push(
					axios
						.get(`${baseURL}/Account/${steamId}`)
						.then((response) => {
							setAccountDetails(response.data);
						})
						.catch((err) => {
							// Not failing the whole loading process if this fails
						})
				);

				// 3. Try to fetch avatar
				requests.push(
					axios
						.get(`${baseURL}/Account/${steamId}/avatar`, {
							responseType: "blob",
						})
						.then((response) => {
							const imageUrl = URL.createObjectURL(response.data);
							setAvatarUrl(imageUrl);
						})
						.catch((err) => {
							// If no avatar exists, try to import one
							return axios
								.get(`${baseURL}/Account/${steamId}/import-avatar`)
								.then((response) => {
									// If import successful, fetch the new avatar
									return axios
										.get(`${baseURL}/Account/${steamId}/avatar`, {
											responseType: "blob",
										})
										.then((response) => {
											const imageUrl = URL.createObjectURL(response.data);
											setAvatarUrl(imageUrl);
										});
								})
								.catch((importErr) => {
									// Not failing if avatar import fails
								});
						})
				);

				// Wait for all requests to complete
				await Promise.allSettled(requests);
			} catch (err) {
				console.error("Error loading profile data:", err);
				setError("Could not load profile information");
			} finally {
				setLoading(false);
			}
		};

		loadProfileData();

		// Cleanup function for blob URLs
		return () => {
			if (avatarUrl) {
				URL.revokeObjectURL(avatarUrl);
			}
		};
	}, [steamId]);

	// Detect all seasons the player has participated in
	const detectPlayerSeasons = async () => {
		if (!steamId) return;

		setDetectingSeasons(true);
		const detectedSeasons: number[] = [];

		try {
			// Check seasons 1 through current
			for (let season = 1; season <= CURRENT_SEASON; season++) {
				try {
					const response = await axios.get(
						`https://api.rising.powerstats.dk/RisingStats/api/PlayerStat/getPlayerBySeasonAndSteamId`,
						{
							params: { steamId, season },
						}
					);

					if (response.data && response.data.gamesPlayed > 0) {
						detectedSeasons.push(season);
					}
				} catch (err) {
					// Season not found for this player, continue
				}
			}

			if (detectedSeasons.length > 0) {
				setAvailableSeasons(detectedSeasons.sort((a, b) => b - a));
				// Set the most recent season as default (highest number = most recent)
				const mostRecentSeason = Math.max(...detectedSeasons);
				setSelectedSeason(mostRecentSeason);
			} else {
				// No seasons found, default to current
				setAvailableSeasons([CURRENT_SEASON]);
				setSelectedSeason(CURRENT_SEASON);
			}
		} catch (error) {
			console.error("Error detecting player seasons:", error);
			setAvailableSeasons([CURRENT_SEASON]);
			setSelectedSeason(CURRENT_SEASON);
		} finally {
			setDetectingSeasons(false);
		}
	};

	// Detect seasons on mount
	useEffect(() => {
		if (steamId) {
			detectPlayerSeasons();
		}
	}, [steamId]);

	// Prioritize team's season if player has a team AND has played matches in that season
	useEffect(() => {
		if (accountDetails?.teamSeason && !detectingSeasons) {
			// Only switch to team's season if the player has actually played in that season
			if (availableSeasons.includes(accountDetails.teamSeason)) {
				setSelectedSeason(accountDetails.teamSeason);
			}
			// Don't add seasons where they haven't played - keep availableSeasons as detected
		}
	}, [accountDetails?.teamSeason, detectingSeasons, availableSeasons]);

	// Fetch player stats from Rising PowerStats API for all available seasons
	useEffect(() => {
		const fetchPlayerStats = async () => {
			if (!steamId || availableSeasons.length === 0) return;

			setLoadingStats(true);
			try {
				// Fetch stats for ALL available seasons
				const processedStats: SeasonStats[] = [];

				// Fetch data for each season
				for (const season of availableSeasons) {
					try {
						// Fetch all matches to determine tournament placements for this season
						const allMatchesResponse = await axios.get(
							`https://api.rising.powerstats.dk/RisingStats/api/Match/getAllMatches?season=${season}`
						);

						// Get tournament placements based on bracket results for this season
						const tournamentPlacements = getTournamentPlacements(
							allMatchesResponse.data?.playedMatches || [],
							season
						);

						// Fetch player statistics for this season
						const seasonStatsResponse = await axios.get(
							`https://api.rising.powerstats.dk/RisingStats/api/PlayerStat/getPlayerBySeasonAndSteamId`,
							{
								params: {
									steamId,
									season: season,
								},
							}
						);

						if (seasonStatsResponse.data) {
							const statsData = seasonStatsResponse.data;
							if (statsData && statsData.gamesPlayed > 0) {
								// Get team placement from team API
								let teamPlacement = null;
								const playerTeamId = statsData.team?.teamId;

								if (playerTeamId) {
									try {
										const teamResponse = await axios.get(`${baseURL}/Team/${playerTeamId}?season=${season}`);
										teamPlacement = teamResponse.data?.currentTournamentPlacement;
									} catch (error) {
										// Fallback to bracket-based placement if team API fails
										const tournamentPlacement = tournamentPlacements[playerTeamId];
										teamPlacement = tournamentPlacement?.placement;
									}
								}

								processedStats.push({
									season: season,
									stats: {
										kills: statsData.kills || 0,
										deaths: statsData.deaths || 0,
										assists: statsData.assists || 0,
										kdr: statsData.kd || 0,
										adr: statsData.adr || 0,
										matches: statsData.gamesPlayed || 0,
									},
									teamName: statsData.team?.teamName,
									placement: teamPlacement,
									// Add these additional stats
									hltv2Rating: statsData.hltv2Rating || 0,
									hsPercentage: statsData.hs || 0,
									kast: statsData.kast || 0,
									firstKills: statsData.fk || 0,
									// Advanced statistics
									openingDuelAttemptPercentage: statsData.openingDuelAttemptPercentage || 0,
									openingDuelWinPercentage: statsData.openingDuelWinPercentage || 0,
									utilityDamage: statsData.utilityDamage || 0,
									enemiesFlashed: statsData.enemiesFlashed || 0,
								});
							}
						}
					} catch (err) {
						console.error(`Error fetching stats for season ${season}:`, err);
						// Continue to next season
					}
				}

				setSeasonStats(processedStats);
			} catch (err) {
				console.error("Error fetching player statistics:", err);
				// Not setting an error state as stats are supplementary
			} finally {
				setLoadingStats(false);
			}
		};

		fetchPlayerStats();
	}, [steamId, availableSeasons]);

	// Fetch recent matches for the selected season
	useEffect(() => {
		const fetchRecentMatches = async () => {
			if (!steamId || !selectedSeason) return;

			try {
				// Fetch all matches for the selected season
				const allMatchesResponse = await axios.get(
					`https://api.rising.powerstats.dk/RisingStats/api/Match/getAllMatches?season=${selectedSeason}`
				);

				// Process recent matches
				if (
					allMatchesResponse.data &&
					allMatchesResponse.data.playedMatches
				) {
					// Get player's team name for the selected season (not current team)
					const selectedSeasonStats = seasonStats.find(s => s.season === selectedSeason);
					const playerSeasonTeamName = selectedSeasonStats?.teamName;

					// Filter matches where the player participated
					const playerMatches = allMatchesResponse.data.playedMatches
						.filter((match: any) => {
							// Check if player's steamID is in either team
							const isInTeam1 =
								match.team1playerids &&
								match.team1playerids.includes(steamId);
							const isInTeam2 =
								match.team2playerids &&
								match.team2playerids.includes(steamId);

							// If we have player IDs, use them to filter
							if (match.team1playerids || match.team2playerids) {
								return isInTeam1 || isInTeam2;
							}

							// For matches without player IDs (e.g., BO3 series), check by team name for the selected season
							if (playerSeasonTeamName) {
								return match.team1?.teamName === playerSeasonTeamName || match.team2?.teamName === playerSeasonTeamName;
							}

							// If no player IDs and no team info for this season, don't include the match
							return false;
						})
						.sort(
							(a: any, b: any) =>
								new Date(b.match_time).getTime() -
								new Date(a.match_time).getTime()
						) // Sort by most recent first
						.slice(0, 50) // Increase to 50 to ensure we have enough matches
						.map((match: any) => {
							// Determine which team the player is on
							const isPlayerTeam1 =
								match.team1playerids &&
								match.team1playerids.includes(steamId);
							const playerTeamName = isPlayerTeam1
								? match.team1.teamName
								: match.team2.teamName;
							const opposingTeamName = isPlayerTeam1
								? match.team2.teamName
								: match.team1.teamName;
							const playerScore = isPlayerTeam1
								? match.team1score
								: match.team2score;
							const opposingScore = isPlayerTeam1
								? match.team2score
								: match.team1score;

							// Detect if this is a BO3 match by checking if scores are <= 3 (series score)
							const isBo3Series = (playerScore <= 3 && opposingScore <= 3 && (playerScore + opposingScore) >= 1);

							return {
								id: match.matchid,
								date: match.match_time,
								teamAName: playerTeamName,
								teamAScore: playerScore,
								teamBName: opposingTeamName,
								teamBScore: opposingScore,
								mapName: isBo3Series ? "BO3 Series" : (match.mapName || "Unknown Map"),
								isBo3Series: isBo3Series,
								// Note: Individual player stats for the match are not provided in this API response
								// You might need a separate API call to get player-specific match stats
								playerStats: undefined,
							};
						});

					setRecentMatches(playerMatches);
				}
			} catch (err) {
				console.error("Error fetching recent matches:", err);
			}
		};

		fetchRecentMatches();
	}, [steamId, selectedSeason, seasonStats]);

	const handleLinkDiscord = () => {
		const token = localStorage.getItem("token");

		if (!token) {
			alert("You need to be logged in to connect Discord");
			return;
		}

		// Use the direct implementation
		window.location.href = `${baseURL}/Auth/discord-link-direct?token=${encodeURIComponent(
			token
		)}`;
	};

	const handleUnlinkDiscord = () => {
		// Show confirmation modal instead of directly unlinking
		setShowUnlinkModal(true);
	};

	const confirmUnlinkDiscord = async () => {
		try {
			const token = localStorage.getItem("token");
			const response = await axios.get(`${baseURL}/Auth/unlink-discord`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (response.status === 200) {
				setShowUnlinkModal(false);
				// Refresh page to update user data
				window.location.reload();
			} else {
				alert("Failed to unlink Discord account. Please try again.");
				setShowUnlinkModal(false);
			}
		} catch (err) {
			console.error("Error unlinking Discord account:", err);
			alert("An error occurred while unlinking your Discord account.");
			setShowUnlinkModal(false);
		}
	};

	const cancelUnlinkDiscord = () => {
		setShowUnlinkModal(false);
	};

	// VDO.ninja handlers
	const handleVdoClick = async () => {
		setVdoLoading(true);
		try {
			let vdoData: VdoLinkResponse;

			// If viewing own profile, fetch own links and open push URL
			if (isOwnProfile) {
				vdoData = await fetchMyVdoLink();
				setVdoLinks(vdoData);
				// Open push link in new tab
				if (vdoData.pushLink) {
					window.open(vdoData.pushLink, '_blank');
				}
			}
			// If admin viewing another profile, fetch player's links and show modal
			else if (user?.isAdmin && user.isAdmin && steamId) {
				vdoData = await fetchPlayerVdoLink(steamId);
				setVdoLinks(vdoData);
				setShowVdoModal(true);
			}
		} catch (error: any) {
			console.error("Error fetching VDO links:", error);
			if (error.response?.status === 404) {
				alert("VDO links not available. Player must be on a team in Season 3.");
			} else {
				alert("Failed to fetch VDO links. Please try again.");
			}
		} finally {
			setVdoLoading(false);
		}
	};

	const handleCopyLink = (url: string) => {
		navigator.clipboard.writeText(url).then(() => {
			alert("Link copied to clipboard!");
		}).catch(() => {
			alert("Failed to copy link. Please copy manually.");
		});
	};

	const closeVdoModal = () => {
		setShowVdoModal(false);
	};

	const handleSeasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedSeason(parseInt(event.target.value));
	};

	if (loading) {
		return (
			<div className="profile-loading">
				<div className="loading-spinner"></div>
				<h2>Loading profile...</h2>
			</div>
		);
	}

	if (error) {
		return (
			<div className="profile-error">
				<h2>Error</h2>
				<p>{error}</p>
				<Link to="/" className="btn btn-primary">
					Return to Home
				</Link>
			</div>
		);
	}

	// Use accountDetails for player info when available, fallback to user context
	const displayName =
		accountDetails?.playerName || user?.playerName || "Player";
	const displayRealName = accountDetails?.name || user?.name;
	const displayAge = accountDetails?.age || user?.age;
	const displayRole = accountDetails?.role || user?.role;
	const displayTwitter = accountDetails?.twitter || user?.twitter;

	// Helper function to format Twitter handle for URL
	const formatTwitterHandle = (handle: string) => {
		if (!handle) return '';
		// Remove @ symbol if present, remove twitter.com/ or x.com/ if it's a full URL
		let cleanHandle = handle.replace(/^@/, '');
		if (cleanHandle.includes('twitter.com/')) {
			cleanHandle = cleanHandle.split('twitter.com/')[1];
		}
		if (cleanHandle.includes('x.com/')) {
			cleanHandle = cleanHandle.split('x.com/')[1];
		}
		// Remove any trailing slash or query parameters
		cleanHandle = cleanHandle.split('/')[0].split('?')[0];
		return cleanHandle;
	};

	// Check if Discord is connected
	const isDiscordConnected =
		accountDetails?.discordUsername && accountDetails.discordUsername !== "";
	const displayDiscordUsername = accountDetails?.discordUsername || "";

	return (
		<div className="profile-page">
			<div className="container">

				{/* Main content area - Stats and matches */}
				<div className="profile-content">
					<div className="left-content">
						{/* Restructured Profile Header - matches stats width */}
						<div className="profile-header-integrated">
							<div className="profile-left-info">
								<div className="profile-avatar">
									<img
										src={
											avatarUrl ||
											steamProfile?.avatarMedium ||
											"https://via.placeholder.com/150"
										}
										alt={`${displayName}'s avatar`}
										className="avatar-img"
									/>
								</div>
								
								<div className="profile-details">
									<div className="profile-name-wrapper">
										<h1 className="profile-name">{displayName}</h1>

										{/* Action buttons next to name */}
										{(isOwnProfile || (user?.isAdmin && user.isAdmin)) && (
											<div className="profile-actions">
												<Link
													to={`/profile/${steamId}/edit`}
													className="btn btn-edit"
													title="Edit Profile"
												>
													<FaEdit />
												</Link>

												{isOwnProfile && (
													<>
														{isDiscordConnected ? (
															<button
																onClick={handleUnlinkDiscord}
																className="btn btn-discord"
																title="Disconnect Discord"
															>
																<FaDiscord />
															</button>
														) : (
															<button
																onClick={handleLinkDiscord}
																className="btn btn-discord"
																title="Connect Discord"
															>
																<FaDiscord />
															</button>
														)}
													</>
												)}

												{/* VDO.ninja button - show if user has team in current season (Season 3) */}
												{accountDetails?.team && accountDetails?.teamSeason === accountDetails?.currentSeason && (
													<button
														onClick={handleVdoClick}
														className="btn btn-vdo"
														title={isOwnProfile ? "Open VDO.ninja Stream" : "View VDO.ninja Links"}
														disabled={vdoLoading}
													>
														<FaVideo />
													</button>
												)}
											</div>
										)}
									</div>

									{displayRealName && (
										<p className="profile-real-name">{displayRealName}</p>
									)}
									{(displayAge ?? 0) > 0 && (
										<p className="profile-age">{displayAge} years old</p>
									)}
									{displayRole && (
										<p className="profile-role">{displayRole}</p>
									)}

									{/* Social links under role */}
									<div className="profile-social">
										<a
											href={
												steamProfile?.profileUrl ||
												`https://steamcommunity.com/profiles/${steamId}`
											}
											target="_blank"
											rel="noopener noreferrer"
											className="social-link steam"
											title="Steam Profile"
										>
											<FaSteam />
										</a>
										
										{displayTwitter && (
											<a
												href={`https://x.com/${formatTwitterHandle(displayTwitter)}`}
												target="_blank"
												rel="noopener noreferrer"
												className="social-link twitter"
												title="X (Twitter)"
											>
												<FaTwitter />
											</a>
										)}
										
										{accountDetails?.faceit && (
											<a
												href={`https://www.faceit.com/en/players/${accountDetails.faceit}`}
												target="_blank"
												rel="noopener noreferrer"
												className="social-link faceit"
												title="FACEIT"
											>
												<SiFaceit />
											</a>
										)}
										
										{isDiscordConnected && (
											<div className="social-link discord connected" title={`Discord: ${displayDiscordUsername}`}>
												<FaDiscord />
											</div>
										)}
									</div>
								</div>
							</div>

							{/* Integrated Team Info */}
							<div className="profile-team-integrated">
								{accountDetails?.team ? (
									<Link
										to={`/teams/${accountDetails.team.id}?season=${accountDetails.teamSeason || selectedSeason}`}
										className="team-info"
									>
										<img
											src={getTeamLogoUrl(accountDetails.team)}
											alt={accountDetails.team.name}
											className="team-logo"
											onError={(e) => {
												(e.target as HTMLImageElement).src = teamPlaceholderImage;
											}}
										/>
										<span className="team-name">{accountDetails.team.name}</span>
									</Link>
								) : (
									<div className="team-info unsigned">
										<span className="unsigned-text">Free Agent</span>
									</div>
								)}
							</div>
						</div>

						{/* Player statistics */}
						<div className="stats-section">
						<div className="section-header">
							<h2>
								Statistics
							</h2>

							<div className="header-right">
								{/* Only show season selector if there are multiple seasons with stats */}
								{availableSeasons.length > 1 && (
									<div className="season-selector">
										<label htmlFor="season-select">Season:</label>
										<select
											id="season-select"
											value={selectedSeason || ''}
											onChange={handleSeasonChange}
										>
											{/* Only show seasons that have statistics available */}
											{availableSeasons.map((season) => (
												<option key={season} value={season}>
													Season {season}
												</option>
											))}
										</select>
									</div>
								)}
							</div>
						</div>


						{loadingStats ? (
							<div className="stats-loading">
								<div className="loading-spinner small"></div>
								<p>Loading statistics...</p>
							</div>
						) : seasonStats.length > 0 ? (
							<>
								{/* Primary Stats Grid */}
								<div className="primary-stats-grid">
									{(() => {
										const selectedStats = seasonStats.find((s) => s.season === selectedSeason);
										return selectedStats?.stats?.matches ? (
										<>
											<div className="stat-card featured">
												<div className="stat-value">
													{selectedStats.stats.matches}
												</div>
												<div className="stat-label">Matches Played</div>
											</div>

											<div className="stat-card featured highlight">
												<div className="stat-value">
													{selectedStats.hltv2Rating?.toFixed(2) || "N/A"}
												</div>
												<div className="stat-label">Rating 2.0</div>
											</div>

											<div className="stat-card">
												<div className="stat-value">
													{selectedStats.hsPercentage ? `${selectedStats.hsPercentage.toFixed(1)}%` : "N/A"}
												</div>
												<div className="stat-label">HS%</div>
											</div>

											<div className="stat-card">
												<div className="stat-value">
													{selectedStats.stats.kdr.toFixed(2)}
												</div>
												<div className="stat-label">K/D</div>
											</div>

											<div className="stat-card">
												<div className="stat-value">
													{selectedStats.stats.adr.toFixed(1)}
												</div>
												<div className="stat-label">ADR</div>
											</div>

											<div className="stat-card">
												<div className="stat-value">
													{selectedStats.kast ? `${selectedStats.kast.toFixed(1)}%` : "N/A"}
												</div>
												<div className="stat-label">KAST%</div>
											</div>
										</>
									) : (
										<div className="no-stats">
											<p>
												No statistics available for Season {selectedSeason}.
											</p>
										</div>
									);
								})()}
								</div>

								{/* Advanced Statistics */}
								{(() => {
									const selectedStats = seasonStats.find((s) => s.season === selectedSeason);
									return selectedStats?.stats?.matches && selectedStats.stats.matches > 0 ? (
										<div className="advanced-stats-section">
											<h4>Advanced Statistics</h4>
											<div className="advanced-stats-grid">
												<div className="advanced-stat-item">
													<span className="advanced-stat-value">
														{selectedStats.openingDuelAttemptPercentage ? `${selectedStats.openingDuelAttemptPercentage.toFixed(1)}%` : "N/A"}
													</span>
													<span className="advanced-stat-label">OP Duel A/R</span>
												</div>
												<div className="advanced-stat-item">
													<span className="advanced-stat-value">
														{selectedStats.openingDuelWinPercentage ? `${selectedStats.openingDuelWinPercentage.toFixed(1)}%` : "N/A"}
													</span>
													<span className="advanced-stat-label">OP Duel W/R</span>
												</div>
												<div className="advanced-stat-item">
													<span className="advanced-stat-value">
														{selectedStats.utilityDamage ? Math.round(selectedStats.utilityDamage) : "N/A"}
													</span>
													<span className="advanced-stat-label">Utility Damage</span>
												</div>
												<div className="advanced-stat-item">
													<span className="advanced-stat-value">
														{selectedStats.enemiesFlashed ? Math.round(selectedStats.enemiesFlashed) : "N/A"}
													</span>
													<span className="advanced-stat-label">Enemies Flashed</span>
												</div>
											</div>
										</div>
									) : null;
								})()}

								{/* Total Statistics */}
								{(() => {
									const selectedStats = seasonStats.find((s) => s.season === selectedSeason);
									return selectedStats?.stats?.matches && selectedStats.stats.matches > 0 ? (
										<div className="total-stats-section">
											<h4>Total Statistics</h4>
											<div className="total-stats-grid">
												<div className="total-stat-item">
													<span className="total-stat-value">{selectedStats.stats.kills}</span>
													<span className="total-stat-label">Kills</span>
												</div>
												<div className="total-stat-item">
													<span className="total-stat-value">{selectedStats.stats.deaths}</span>
													<span className="total-stat-label">Deaths</span>
												</div>
												<div className="total-stat-item">
													<span className="total-stat-value">{selectedStats.stats.assists}</span>
													<span className="total-stat-label">Assists</span>
												</div>
												<div className="total-stat-item">
													<span className="total-stat-value">{selectedStats.firstKills || "N/A"}</span>
													<span className="total-stat-label">First Kills</span>
												</div>
											</div>
										</div>
									) : null;
								})()}

								{/* Per Match Averages */}
								{(() => {
									const selectedStats = seasonStats.find((s) => s.season === selectedSeason);
									return selectedStats?.stats?.matches && selectedStats.stats.matches > 0 ? (
										<div className="per-match-section">
											<h4>Per Match Averages</h4>
											<div className="per-match-grid">
												{(() => {
													const stats = selectedStats.stats;
													const matches = stats.matches;
													const firstKills = selectedStats.firstKills || 0;
													return (
														<>
															<div className="per-match-item">
																<span className="per-match-value">{(stats.kills / matches).toFixed(1)}</span>
																<span className="per-match-label">Kills</span>
															</div>
															<div className="per-match-item">
																<span className="per-match-value">{(stats.deaths / matches).toFixed(1)}</span>
																<span className="per-match-label">Deaths</span>
															</div>
															<div className="per-match-item">
																<span className="per-match-value">{(stats.assists / matches).toFixed(1)}</span>
																<span className="per-match-label">Assists</span>
															</div>
															<div className="per-match-item">
																<span className="per-match-value">{(firstKills / matches).toFixed(1)}</span>
																<span className="per-match-label">First Kills</span>
															</div>
														</>
													);
												})()}
											</div>
										</div>
									) : null;
								})()}

							</>
						) : (
							<div className="no-stats">
								<p>No statistics available yet.</p>
								{isOwnProfile && (
									<p className="stats-hint">
										Statistics will appear here after you've played your first
										match!
									</p>
								)}
							</div>
						)}

						{/* Enhanced career achievements section - only show if there are achievements */}
						{(seasonStats.length > 0 || (steamId && MVP_ACHIEVEMENTS[steamId])) && (
							<div className="achievements-section">
								<h3>Tournament Achievements</h3>

								<div className="achievements-grid">
									{/* MVP Achievements */}
									{steamId && MVP_ACHIEVEMENTS[steamId] && MVP_ACHIEVEMENTS[steamId].map((season, index) => {
										// Find team name from season stats if available
										const seasonData = seasonStats.find(s => s.season === season);
										return (
											<div key={`mvp-${index}`} className="achievement-card mvp">
												<div className="achievement-medal mvp">
													⭐
												</div>
												<div className="achievement-info">
													<div className="achievement-title">MVP of the Season</div>
													<div className="achievement-season">Season {season}</div>
													{seasonData?.teamName && (
														<div className="achievement-team">{seasonData.teamName}</div>
													)}
												</div>
											</div>
										);
									})}

									{/* Tournament Placements */}
									{seasonStats
										.filter(
											(season) => season.placement && season.placement <= 3
										)
										.map((season, index) => (
											<div key={index} className="achievement-card">
												<div className={`achievement-medal place-${season.placement}`}>
													{season.placement === 1 ? "🏆" : season.placement === 2 ? "🥈" : "🥉"}
												</div>
												<div className="achievement-info">
													<div className="achievement-title">
														{season.placement === 1 ? "Champion" : `${season.placement}${season.placement === 2 ? "nd" : "rd"} Place`}
													</div>
													<div className="achievement-season">Season {season.season}</div>
													{season.teamName && (
														<div className="achievement-team">{season.teamName}</div>
													)}
												</div>
											</div>
										))}

									{/* Season Participation */}
									{seasonStats.length >= 2 && (
										<div className="achievement-card participation">
											<div className="achievement-medal participation">
												⚡
											</div>
											<div className="achievement-info">
												<div className="achievement-title">Veteran Player</div>
												<div className="achievement-season">{seasonStats.length} Seasons</div>
												<div className="achievement-team">Competition Experience</div>
											</div>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Player Highlights Section */}
						{steamId && (
							<PlayerHighlightsSection
								steamId={steamId}
								playerName={accountDetails?.playerName || accountDetails?.name}
								playerAvatar={steamProfile?.avatarMedium}
							/>
						)}

						{/* Team History Section */}
						{seasonStats.length > 0 && seasonStats.some(s => s.teamName) && (
							<div className="team-history-section">
								<h3>Team History</h3>

								{seasonStats
									.filter(s => s.teamName)
									.sort((a, b) => b.season - a.season) // Sort by most recent first
									.map((season, index) => (
										<div key={index} className="team-history-item">
											<div className="team-history-season">
												Season {season.season}
											</div>
											<div className="team-history-details">
												<div className="team-history-logo">
													{/* Try to get team logo from current team if it matches, otherwise use placeholder */}
													{accountDetails?.team && accountDetails.team.name === season.teamName ? (
														<img
															src={getTeamLogoUrl(accountDetails.team)}
															alt={season.teamName}
															onError={(e) => {
																(e.target as HTMLImageElement).src = teamPlaceholderImage;
															}}
														/>
													) : (
														<div className="team-logo-placeholder">
															<span>{season.teamName?.charAt(0) || 'T'}</span>
														</div>
													)}
												</div>
												<div className="team-history-name">
													{season.teamName}
												</div>
												<div className="team-history-stats">
													{season.hltv2Rating && season.hltv2Rating > 0 && (
														<div className="team-history-rating">
															<span className="rating-label">Rating:</span>
															<span className="rating-value">{season.hltv2Rating.toFixed(2)}</span>
														</div>
													)}
													{season.placement && (
														<div className="team-history-placement">
															<span className="placement-label">Placement:</span>
															<span className="placement-value">#{season.placement}</span>
														</div>
													)}
												</div>
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				</div>

				{/* Right sidebar - Recent matches */}
				<div className="right-sidebar">
					<div className="sidebar-content">
						<div className="sidebar-header">
							<h2>Recent Matches ({recentMatches.length})</h2>
						</div>

						<div className="sidebar-body">
							{loadingStats ? (
								<div className="matches-loading">
									<div className="loading-spinner small"></div>
									<p>Loading matches...</p>
								</div>
							) : recentMatches.length > 0 ? (
								<div className="matches-list">
									{recentMatches.map((match, index) => {
										// The team names and scores are already properly assigned in the data processing
										const playerTeam = match.teamAName;
										const opposingTeam = match.teamBName;
										const playerScore = match.teamAScore;
										const opposingScore = match.teamBScore;
										const isWin = playerScore > opposingScore;
										// Use the BO3 detection from the data processing
										const isBo3 = match.isBo3Series;

										return (
											<Link
												key={match.id || index}
												to={`/match/${match.id}`}
												className={`match-card ${isWin ? "win" : "loss"}`}
											>
												<div className="profile-match-header">
													<div className="profile-match-info">
														<span className="match-date">{formatDate(match.date)}</span>
														<span className="match-format">{isBo3 ? "BO3" : "BO1"}</span>
														<span className="match-map">{formatMapName(match.mapName)}</span>
													</div>
												</div>

												<div className="match-teams">
													<div className="match-team">
														<div className="team-name">{playerTeam}</div>
														<div className="team-score">{playerScore}</div>
													</div>

													<div className="match-vs">
														<span className="vs-text">vs</span>
													</div>

													<div className="match-team">
														<div className="team-name">{opposingTeam}</div>
														<div className="team-score">{opposingScore}</div>
													</div>
												</div>

												{/* Only show player stats if they are available */}
												{match.playerStats && (
													<div className="player-match-stats">
														<div className="player-stat">
															<span className="stat-label">K/D/A:</span>
															<span className="stat-value">
																{match.playerStats.kills}/
																{match.playerStats.deaths}/
																{match.playerStats.assists}
															</span>
														</div>

														<div className="player-stat">
															<span className="stat-label">ADR:</span>
															<span className="stat-value">
																{match.playerStats.adr.toFixed(1)}
															</span>
														</div>
													</div>
												)}
											</Link>
										);
									})}
								</div>
							) : (
								<p className="no-matches">
									No played matches found for Season {selectedSeason}.
								</p>
							)}
						</div>

						<div className="sidebar-footer">
							{recentMatches.length > 0 && (
								<div className="view-history">
									<Link to={`/matches?player=${steamId}&season=${selectedSeason}`} className="view-history-link">
										View full history
									</Link>
								</div>
							)}
						</div>
					</div>
				</div>
				</div>
			</div>
			
			{/* Discord Unlink Confirmation Modal */}
			{showUnlinkModal && (
				<div className="modal-overlay" onClick={cancelUnlinkDiscord} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') cancelUnlinkDiscord(); }}>
					<div className="modal-content discord-unlink-modal" onClick={(e) => e.stopPropagation()} role="dialog" onKeyDown={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h2>Disconnect Discord Account</h2>
							<button className="modal-close" onClick={cancelUnlinkDiscord}>
								×
							</button>
						</div>
						
						<div className="modal-body">
							<div className="discord-icon">
								<FaDiscord />
							</div>
							
							<div className="modal-warning">
								<h3>Are you sure you want to disconnect your Discord account?</h3>
								<p className="warning-text">
									{accountDetails?.discordUsername && (
										<>Currently connected as: <strong>{accountDetails.discordUsername}</strong></>
									)}
								</p>
								<p className="warning-info">
									You will lose access to:
								</p>
								<ul className="warning-list">
									<li>Discord-based notifications</li>
									<li>Team communication features</li>
									<li>Tournament announcements via Discord</li>
								</ul>
								<p className="warning-note">
									You can reconnect your Discord account at any time.
								</p>
							</div>
						</div>
						
						<div className="modal-footer">
							<button 
								className="btn btn-cancel" 
								onClick={cancelUnlinkDiscord}
							>
								Cancel
							</button>
							<button 
								className="btn btn-danger" 
								onClick={confirmUnlinkDiscord}
							>
								Disconnect Discord
							</button>
						</div>
					</div>
				</div>
			)}

			{/* VDO.ninja Links Modal (Admin View) */}
			{showVdoModal && vdoLinks && (
				<div className="modal-overlay" onClick={closeVdoModal} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') closeVdoModal(); }}>
					<div className="modal-content vdo-links-modal" onClick={(e) => e.stopPropagation()} role="dialog" onKeyDown={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h2>VDO.ninja Links</h2>
							<button className="modal-close" onClick={closeVdoModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="vdo-info">
								<p><strong>Player:</strong> {vdoLinks.playerName}</p>
								<p><strong>Team:</strong> {vdoLinks.teamName}</p>
							</div>

							<div className="vdo-links-container">
								{vdoLinks.pushLink && (
									<div className="vdo-link-item">
										<label htmlFor="vdo-push-link">Push Link (Player):</label>
										<div className="link-with-copy">
											<input
												id="vdo-push-link"
												type="text"
												value={vdoLinks.pushLink}
												readOnly
												className="link-input"
											/>
											<button
												className="btn btn-copy"
												onClick={() => handleCopyLink(vdoLinks.pushLink!)}
												title="Copy to clipboard"
											>
												<FaCopy />
											</button>
										</div>
									</div>
								)}

								<div className="vdo-link-item">
									<label htmlFor="vdo-view-link">View Link (Observer):</label>
									<div className="link-with-copy">
										<input
											id="vdo-view-link"
											type="text"
											value={vdoLinks.viewLink}
											readOnly
											className="link-input"
										/>
										<button
											className="btn btn-copy"
											onClick={() => handleCopyLink(vdoLinks.viewLink)}
											title="Copy to clipboard"
										>
											<FaCopy />
										</button>
									</div>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							<button
								className="btn btn-primary"
								onClick={closeVdoModal}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ProfilePage;
